import React, { useState, useEffect } from 'react';
import EntityScopeNotice from '../../components/Invoicing/EntityScopeNotice';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileInvoice, 
  faFileContract, 
  faFileInvoiceDollar, 
  faShoppingCart,
  faPlus,
  faMinus,
  faArrowRight,
  faExclamationTriangle,
  faMoneyBillWave,
  faCalendarAlt,
  faClock,
  faEdit,
  faShippingFast,
  faCoins,
  faHandHoldingUsd,
  faReceipt,
  faUndoAlt,
  faExclamationCircle,
  faDatabase,
  faBookOpen,
  faSitemap,
  faTags,
  faCheckCircle,
  faBan,
  faLayerGroup,
  faBuilding,
  faPercent,
  faGlobe,
  faSyncAlt,
  faServer
} from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';
import HMSButton from '../../components/ui/HMSButton';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import HMSSkeleton from '../../components/ui/HMSSkeleton';
import Footer from '../../components/ui/Footer';
import invoicingService from '../../services/invoicingService';
import abacusService from '../../services/abacusService';
import './InvoicingDashboard.css';

/**
 * Invoicing Dashboard - Main Landing Page
 * 
 * Features:
 * - Summary statistics for all 4 invoice types
 * - Quick action buttons to create new invoices
 * - Navigation to detailed list pages
 */

const InvoicingDashboard = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  // Restore active tab from sessionStorage or default to 'receivable'
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem('invoicingActiveTab') || 'receivable';
  });
  const [stats, setStats] = useState({
    outgoing: { total_count: 0, total_amount: 0, overdue_count: 0, currency: 'USD' },
    proforma: { total_count: 0, pending_conversion: 0 },
    incoming: { total_count: 0, total_payable: 0, overdue_count: 0, currency: 'USD' },
    purchase_orders: { total_count: 0, pending_receipt: 0 },
    credit_notes: { 
      total_count: 0, 
      currency_totals: {}, 
      pending_approval_count: 0,
      approved_unapplied_count: 0,
      this_month_count: 0
    },
    debit_notes: { 
      total_count: 0, 
      currency_totals: {}, 
      pending_approval_count: 0,
      approved_unpaid_count: 0,
      this_month_count: 0
    }
  });
  const [error, setError] = useState(null);
  const [masterDataStats, setMasterDataStats] = useState({
    gl: { total: 0, active: 0, inactive: 0, typeBreakdown: {} },
    costCentres: { total: 0, active: 0, inactive: 0, departments: 0 },
    costCategories: { total: 0, active: 0, inactive: 0, groups: 0 },
    vatRates: { total: 0, active: 0, inactive: 0, countries: 0 }
  });
  const [masterDataLoading, setMasterDataLoading] = useState(false);
  const [abacusStats, setAbacusStats] = useState({ total_synced: 0, total_pending: 0, total_errors: 0, healthy: null });
  const [abacusLoading, setAbacusLoading] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);
  
  // Persist active tab to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('invoicingActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'masterdata') {
      loadMasterDataStats();
      loadAbacusStats();
    }
  }, [activeTab]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load statistics
      const statsData = await invoicingService.fetchInvoicingDashboardStats();
      
      // Merge with default values to handle missing fields from API
      setStats(prevStats => ({
        ...prevStats,
        ...statsData,
        // Ensure nested objects are properly merged
        outgoing: { ...prevStats.outgoing, ...(statsData.outgoing || {}) },
        proforma: { ...prevStats.proforma, ...(statsData.proforma || {}) },
        incoming: { ...prevStats.incoming, ...(statsData.incoming || {}) },
        purchase_orders: { ...prevStats.purchase_orders, ...(statsData.purchase_orders || {}) },
        credit_notes: { ...prevStats.credit_notes, ...(statsData.credit_notes || {}) },
        debit_notes: { ...prevStats.debit_notes, ...(statsData.debit_notes || {}) }
      }));
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data. Please try again.');
      setLoading(false);
    }
  };

  const loadMasterDataStats = async () => {
    try {
      setMasterDataLoading(true);
      const [glData, ccData, catData, vatData] = await Promise.all([
        invoicingService.fetchGLAccounts({ show_inactive: 'true' }),
        invoicingService.fetchCostCenters({ show_inactive: 'true' }),
        invoicingService.fetchCostCategories({ show_inactive: 'true' }),
        invoicingService.fetchVATRates({ show_inactive: 'true' })
      ]);

      const glList = Array.isArray(glData) ? glData : (glData.results || []);
      const ccList = Array.isArray(ccData) ? ccData : (ccData.results || []);
      const catList = Array.isArray(catData) ? catData : (catData.results || []);
      const vatList = Array.isArray(vatData) ? vatData : (vatData.results || []);

      const glActive = glList.filter(a => a.is_active);
      const typeBreakdown = {};
      glActive.forEach(a => {
        const t = a.account_type || 'OTHER';
        typeBreakdown[t] = (typeBreakdown[t] || 0) + 1;
      });
      const glEntities = new Set(glList.map(a => a.entity)).size;

      const ccActive = ccList.filter(c => c.is_active);
      const departments = new Set(ccList.filter(c => c.department).map(c => c.department)).size;
      const ccEntities = new Set(ccList.map(c => c.entity)).size;

      const catActive = catList.filter(c => c.is_active);
      const groups = new Set(catList.map(c => c.group)).size;

      const vatActive = vatList.filter(v => v.is_active);
      const countries = new Set(vatList.filter(v => v.country_code).map(v => v.country_code)).size;

      setMasterDataStats({
        gl: { total: glList.length, active: glActive.length, inactive: glList.length - glActive.length, typeBreakdown, entities: glEntities },
        costCentres: { total: ccList.length, active: ccActive.length, inactive: ccList.length - ccActive.length, departments, entities: ccEntities },
        costCategories: { total: catList.length, active: catActive.length, inactive: catList.length - catActive.length, groups },
        vatRates: { total: vatList.length, active: vatActive.length, inactive: vatList.length - vatActive.length, countries }
      });
    } catch (err) {
      console.error('Error loading master data stats:', err);
    } finally {
      setMasterDataLoading(false);
    }
  };

  const loadAbacusStats = async () => {
    try {
      setAbacusLoading(true);
      const [statusData, healthData] = await Promise.all([
        abacusService.getSyncStatus(),
        abacusService.healthCheck(),
      ]);
      setAbacusStats({
        total_synced: statusData.total_in_abacus || 0,
        total_pending: statusData.total_waiting || 0,
        total_errors: statusData.total_errors || 0,
        healthy: healthData.status === 'healthy',
      });
    } catch (err) {
      console.error('Error loading Abacus stats:', err);
      setAbacusStats(prev => ({ ...prev, healthy: false }));
    } finally {
      setAbacusLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount && amount !== 0) return `${currency} 0.00`;
    return `${currency} ${parseFloat(amount).toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  const SummaryCard = ({ 
    title, 
    icon, 
    totalCount, 
    currencyTotals,
    thisMonthCount,
    alertCount, 
    alertLabel,
    secondaryMetric,
    secondaryMetricLabel,
    secondaryMetricIcon,
    createLabel,
    onCreateClick,
    onViewAllClick,
    color,
    cashFlowSign
  }) => (
    <div 
      className="dashboard-summary-card" 
      style={{ borderTopColor: color }}
      onClick={onViewAllClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => { if (e.key === 'Enter') onViewAllClick(); }}
    >
      <div className="card-header">
        <FontAwesomeIcon icon={icon} style={{ color }} />
        <h3>{title}</h3>
        {cashFlowSign && (
          <span className={`cash-flow-sign ${cashFlowSign === '+' ? 'sign-positive' : 'sign-negative'}`}>
            <FontAwesomeIcon icon={cashFlowSign === '+' ? faPlus : faMinus} />
          </span>
        )}
      </div>
      
      <div className="card-stats">
        <div className="stat-main">
          <span className="stat-value">{totalCount || 0}</span>
          <span className="stat-label">Total Invoices</span>
        </div>
        
        {thisMonthCount !== undefined && thisMonthCount !== null && (
          <div className="stat-secondary">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <div>
              <span className="stat-value-small">{thisMonthCount}</span>
              <span className="stat-label-small">This Month</span>
            </div>
          </div>
        )}

        {currencyTotals && Object.keys(currencyTotals).length > 0 && (
          <div className="currency-breakdown">
            <div className="currency-breakdown-header">
              <FontAwesomeIcon icon={faCoins} />
              <span>Currency Breakdown</span>
            </div>
            <div className="currency-list">
              {Object.entries(currencyTotals).map(([currency, amount]) => (
                <div key={currency} className="currency-item">
                  <span className="currency-code">{currency}</span>
                  <span className="currency-amount">{formatCurrency(amount, currency)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {secondaryMetric !== undefined && secondaryMetric !== null && (
          <div className="stat-secondary">
            <FontAwesomeIcon icon={secondaryMetricIcon || faClock} />
            <div>
              <span className="stat-value-small">{secondaryMetric}</span>
              <span className="stat-label-small">{secondaryMetricLabel}</span>
            </div>
          </div>
        )}
      </div>
      
      {alertCount > 0 && (
        <div className="card-alerts">
          <div className="alert-item">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{alertCount} {alertLabel}</span>
          </div>
        </div>
      )}
      
      {/* flex:1 stays inline — the grow is this card's row layout, not part
          of the shared button shape. */}
      <div className="card-actions">
        <HMSButton
          variant="primary"
          size="compact"
          style={{ flex: 1 }}
          onClick={(e) => { e.stopPropagation(); onCreateClick(); }}
        >
          <FontAwesomeIcon icon={faPlus} /> {createLabel}
        </HMSButton>
        <HMSButton
          variant="secondary"
          size="compact"
          onClick={(e) => { e.stopPropagation(); onViewAllClick(); }}
        >
          View All <FontAwesomeIcon icon={faArrowRight} />
        </HMSButton>
      </div>
    </div>
  );

  const invoicingTabs = [
    { key: 'receivable', label: 'Accounts Receivable', icon: faHandHoldingUsd },
    { key: 'payable', label: 'Accounts Payable', icon: faReceipt },
    { key: 'masterdata', label: 'Finance Master Data', icon: faDatabase },
  ];

  if (loading) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="invoice-page-container">
          {/* Three blocks in the real grid, at the height a SummaryCard
              settles on, so nothing jumps when the stats land. */}
          <div className="dashboard-content">
            <div className="dashboard-cards-grid">
              <HMSSkeleton shape="rect" height={500} />
              <HMSSkeleton shape="rect" height={500} />
              <HMSSkeleton shape="rect" height={500} />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="invoice-page-container">
          <div className="dashboard-error">
            <FontAwesomeIcon icon={faExclamationTriangle} size="3x" />
            <h3>Error Loading Dashboard</h3>
            <p>{error}</p>
            <HMSButton variant="primary" onClick={loadDashboardData}>
              <FontAwesomeIcon icon={faArrowRight} /> Retry
            </HMSButton>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
      <div className="invoice-page-container">
        {/* Shared screen header: brand block + pill tray, no right widgets —
            the component's empty actions zone balances the tray on centre. */}
        <HMSPageHeader
          title="Invoicing"
          kicker="Finance"
          tabs={{ items: invoicingTabs, activeKey: activeTab, onChange: setActiveTab }}
        />

        {/* Entry point to the whole tab, so this is where an unscoped view gets
            declared once rather than on six list screens. */}
        <EntityScopeNotice />

        {/* Dashboard Content */}
        <div className="dashboard-content">

          {/* Accounting handover queue entry point (last link of the invoice
              workflow: stamped documents awaiting collection by accounting) */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
            <button
              className="btn-card-link"
              onClick={() => history.push('/invoicing/accounting-queue')}
            >
              Awaiting Accounting <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>

          {/* Accounts Receivable - HMS Issues */}
          {activeTab === 'receivable' && (
            <div className="dashboard-cards-grid">
              {/* Pro-forma Invoices Card */}
              <SummaryCard
                title="Pro-forma Invoices"
                icon={faFileContract}
                color="#ffc20c"
                cashFlowSign="+"
                totalCount={stats.proforma.total_count}
                currencyTotals={stats.proforma.currency_totals}
                thisMonthCount={stats.proforma.this_month_count}
                alertCount={stats.proforma.pending_conversion}
                alertLabel="Pending Conversion"
                secondaryMetric={stats.proforma.draft_count}
                secondaryMetricLabel="Draft"
                secondaryMetricIcon={faEdit}
                createLabel="Create New"
                onCreateClick={() => history.push('/invoicing/proforma/create')}
                onViewAllClick={() => history.push('/invoicing/proforma')}
              />
              
              {/* Outgoing Invoices Card */}
              <SummaryCard
                title="Outgoing Invoices"
                icon={faFileInvoiceDollar}
                color="#4a6b25"
                cashFlowSign="+"
                totalCount={stats.outgoing.total_count}
                currencyTotals={stats.outgoing.currency_totals}
                thisMonthCount={stats.outgoing.this_month_count}
                alertCount={stats.outgoing.overdue_count}
                alertLabel="Overdue"
                secondaryMetric={stats.outgoing.unpaid_count}
                secondaryMetricLabel="Unpaid"
                secondaryMetricIcon={faClock}
                createLabel="Create New"
                onCreateClick={() => history.push('/invoicing/outgoing/create')}
                onViewAllClick={() => history.push('/invoicing/outgoing')}
              />
              
              {/* Credit Notes (AR) Card */}
              <SummaryCard
                title="Credit Notes"
                icon={faUndoAlt}
                color="#ef4444"
                cashFlowSign="-"
                totalCount={stats.credit_notes.total_count}
                currencyTotals={stats.credit_notes.currency_totals}
                thisMonthCount={stats.credit_notes.this_month_count}
                alertCount={stats.credit_notes.pending_approval_count}
                alertLabel="Pending Approval"
                secondaryMetric={stats.credit_notes.approved_unapplied_count}
                secondaryMetricLabel="Unapplied"
                secondaryMetricIcon={faExclamationCircle}
                createLabel="Create New"
                onCreateClick={() => history.push('/invoicing/credit-notes/create')}
                onViewAllClick={() => history.push('/invoicing/credit-notes')}
              />
            </div>
          )}

          {/* Accounts Payable - HMS Receives */}
          {activeTab === 'payable' && (
            <div className="dashboard-cards-grid">
              {/* Purchase Orders Card */}
              <SummaryCard
                title="Purchase Orders"
                icon={faShoppingCart}
                color="#0891b2"
                cashFlowSign="-"
                totalCount={stats.purchase_orders.total_count}
                currencyTotals={stats.purchase_orders.currency_totals}
                thisMonthCount={stats.purchase_orders.this_month_count}
                alertCount={stats.purchase_orders.pending_receipt}
                alertLabel="Pending Receipt"
                secondaryMetric={stats.purchase_orders.in_transit_count}
                secondaryMetricLabel="In Transit"
                secondaryMetricIcon={faShippingFast}
                createLabel="Create New"
                onCreateClick={() => history.push('/invoicing/purchase-orders/create')}
                onViewAllClick={() => history.push('/invoicing/purchase-orders')}
              />
              
              {/* Incoming Invoices Card */}
              <SummaryCard
                title="Incoming Invoices"
                icon={faReceipt}
                color="#10b981"
                cashFlowSign="-"
                totalCount={stats.incoming.total_count}
                currencyTotals={stats.incoming.currency_totals}
                thisMonthCount={stats.incoming.this_month_count}
                alertCount={stats.incoming.overdue_count}
                alertLabel="Overdue"
                secondaryMetric={stats.incoming.pending_payment_count}
                secondaryMetricLabel="Pending Payment"
                secondaryMetricIcon={faMoneyBillWave}
                createLabel="Create New"
                onCreateClick={() => history.push('/invoicing/incoming/create')}
                onViewAllClick={() => history.push('/invoicing/incoming')}
              />
              
              {/* Debit Notes (AP) Card */}
              <SummaryCard
                title="Debit Notes"
                icon={faExclamationCircle}
                color="#7c3aed"
                cashFlowSign="+"
                totalCount={stats.debit_notes.total_count}
                currencyTotals={stats.debit_notes.currency_totals}
                thisMonthCount={stats.debit_notes.this_month_count}
                alertCount={stats.debit_notes.pending_approval_count}
                alertLabel="Pending Approval"
                secondaryMetric={stats.debit_notes.approved_unpaid_count}
                secondaryMetricLabel="Unpaid"
                secondaryMetricIcon={faMoneyBillWave}
                createLabel="Create New"
                onCreateClick={() => history.push('/invoicing/debit-notes/create')}
                onViewAllClick={() => history.push('/invoicing/debit-notes')}
              />
            </div>
          )}

          {/* Finance Master Data */}
          {activeTab === 'masterdata' && (
            <div className="dashboard-cards-grid">
              {/* GL Accounts Card */}
              <div 
                className="dashboard-summary-card" 
                style={{ borderTopColor: '#4a6b25' }}
                onClick={() => history.push('/invoicing/master-data/gl-accounts')}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => { if (e.key === 'Enter') history.push('/invoicing/master-data/gl-accounts'); }}
              >
                <div className="card-header">
                  <FontAwesomeIcon icon={faBookOpen} style={{ color: '#4a6b25' }} />
                  <h3>GL Accounts</h3>
                  <span className="master-data-scope-badge scope-entity">
                    <FontAwesomeIcon icon={faBuilding} /> Per Entity
                  </span>
                </div>
                <div className="card-stats">
                  <div className="stat-main">
                    <span className="stat-value">{masterDataLoading ? '...' : masterDataStats.gl.total}</span>
                    <span className="stat-label">Total Accounts</span>
                  </div>
                  <div className="stat-secondary">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <div>
                      <span className="stat-value-small">{masterDataLoading ? '...' : masterDataStats.gl.active}</span>
                      <span className="stat-label-small">Active</span>
                    </div>
                  </div>
                  {masterDataStats.gl.inactive > 0 && (
                    <div className="stat-secondary">
                      <FontAwesomeIcon icon={faBan} />
                      <div>
                        <span className="stat-value-small">{masterDataStats.gl.inactive}</span>
                        <span className="stat-label-small">Inactive</span>
                      </div>
                    </div>
                  )}
                  {!masterDataLoading && Object.keys(masterDataStats.gl.typeBreakdown).length > 0 && (
                    <div className="currency-breakdown">
                      <div className="currency-breakdown-header">
                        <FontAwesomeIcon icon={faLayerGroup} />
                        <span>Account Types</span>
                      </div>
                      <div className="currency-list">
                        {Object.entries(masterDataStats.gl.typeBreakdown)
                          .sort((a, b) => b[1] - a[1])
                          .map(([type, count]) => (
                            <div key={type} className="currency-item">
                              <span className="currency-code">{type.replace(/_/g, ' ')}</span>
                              <span className="currency-amount">{count}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="card-actions">
                  <HMSButton
                    variant="primary"
                    size="compact"
                    style={{ flex: 1 }}
                    onClick={(e) => { e.stopPropagation(); history.push('/invoicing/master-data/gl-accounts'); }}
                  >
                    <FontAwesomeIcon icon={faArrowRight} /> Manage GL Accounts
                  </HMSButton>
                </div>
              </div>

              {/* Cost Centres Card */}
              <div 
                className="dashboard-summary-card" 
                style={{ borderTopColor: '#10b981' }}
                onClick={() => history.push('/invoicing/master-data/cost-centres')}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => { if (e.key === 'Enter') history.push('/invoicing/master-data/cost-centres'); }}
              >
                <div className="card-header">
                  <FontAwesomeIcon icon={faSitemap} style={{ color: '#10b981' }} />
                  <h3>Cost Centres</h3>
                  <span className="master-data-scope-badge scope-entity">
                    <FontAwesomeIcon icon={faBuilding} /> Per Entity
                  </span>
                </div>
                <div className="card-stats">
                  <div className="stat-main">
                    <span className="stat-value">{masterDataLoading ? '...' : masterDataStats.costCentres.total}</span>
                    <span className="stat-label">Total Cost Centres</span>
                  </div>
                  <div className="stat-secondary">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <div>
                      <span className="stat-value-small">{masterDataLoading ? '...' : masterDataStats.costCentres.active}</span>
                      <span className="stat-label-small">Active</span>
                    </div>
                  </div>
                  {masterDataStats.costCentres.inactive > 0 && (
                    <div className="stat-secondary">
                      <FontAwesomeIcon icon={faBan} />
                      <div>
                        <span className="stat-value-small">{masterDataStats.costCentres.inactive}</span>
                        <span className="stat-label-small">Inactive</span>
                      </div>
                    </div>
                  )}
                  {masterDataStats.costCentres.departments > 0 && (
                    <div className="stat-secondary">
                      <FontAwesomeIcon icon={faSitemap} />
                      <div>
                        <span className="stat-value-small">{masterDataStats.costCentres.departments}</span>
                        <span className="stat-label-small">Departments</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="card-actions">
                  <HMSButton
                    variant="primary"
                    size="compact"
                    style={{ flex: 1 }}
                    onClick={(e) => { e.stopPropagation(); history.push('/invoicing/master-data/cost-centres'); }}
                  >
                    <FontAwesomeIcon icon={faArrowRight} /> Manage Cost Centres
                  </HMSButton>
                </div>
              </div>

              {/* Cost Categories Card */}
              <div 
                className="dashboard-summary-card" 
                style={{ borderTopColor: '#7c3aed' }}
                onClick={() => history.push('/invoicing/master-data/cost-categories')}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => { if (e.key === 'Enter') history.push('/invoicing/master-data/cost-categories'); }}
              >
                <div className="card-header">
                  <FontAwesomeIcon icon={faTags} style={{ color: '#7c3aed' }} />
                  <h3>Cost Categories</h3>
                  <span className="master-data-scope-badge scope-global">
                    <FontAwesomeIcon icon={faDatabase} /> Global
                  </span>
                </div>
                <div className="card-stats">
                  <div className="stat-main">
                    <span className="stat-value">{masterDataLoading ? '...' : masterDataStats.costCategories.total}</span>
                    <span className="stat-label">Total Categories</span>
                  </div>
                  <div className="stat-secondary">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <div>
                      <span className="stat-value-small">{masterDataLoading ? '...' : masterDataStats.costCategories.active}</span>
                      <span className="stat-label-small">Active</span>
                    </div>
                  </div>
                  {masterDataStats.costCategories.inactive > 0 && (
                    <div className="stat-secondary">
                      <FontAwesomeIcon icon={faBan} />
                      <div>
                        <span className="stat-value-small">{masterDataStats.costCategories.inactive}</span>
                        <span className="stat-label-small">Inactive</span>
                      </div>
                    </div>
                  )}
                  {masterDataStats.costCategories.groups > 0 && (
                    <div className="stat-secondary">
                      <FontAwesomeIcon icon={faLayerGroup} />
                      <div>
                        <span className="stat-value-small">{masterDataStats.costCategories.groups}</span>
                        <span className="stat-label-small">Groups</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="card-actions">
                  <HMSButton
                    variant="primary"
                    size="compact"
                    style={{ flex: 1 }}
                    onClick={(e) => { e.stopPropagation(); history.push('/invoicing/master-data/cost-categories'); }}
                  >
                    <FontAwesomeIcon icon={faArrowRight} /> Manage Cost Categories
                  </HMSButton>
                </div>
              </div>

              {/* VAT Rates Card */}
              <div 
                className="dashboard-summary-card" 
                style={{ borderTopColor: '#f59e0b' }}
                onClick={() => history.push('/invoicing/master-data/vat-rates')}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => { if (e.key === 'Enter') history.push('/invoicing/master-data/vat-rates'); }}
              >
                <div className="card-header">
                  <FontAwesomeIcon icon={faPercent} style={{ color: '#f59e0b' }} />
                  <h3>VAT Rates</h3>
                  <span className="master-data-scope-badge scope-entity">
                    <FontAwesomeIcon icon={faBuilding} /> Per Entity
                  </span>
                </div>
                <div className="card-stats">
                  <div className="stat-main">
                    <span className="stat-value">{masterDataLoading ? '...' : masterDataStats.vatRates.total}</span>
                    <span className="stat-label">Total VAT Rates</span>
                  </div>
                  <div className="stat-secondary">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <div>
                      <span className="stat-value-small">{masterDataLoading ? '...' : masterDataStats.vatRates.active}</span>
                      <span className="stat-label-small">Active</span>
                    </div>
                  </div>
                  {masterDataStats.vatRates.inactive > 0 && (
                    <div className="stat-secondary">
                      <FontAwesomeIcon icon={faBan} />
                      <div>
                        <span className="stat-value-small">{masterDataStats.vatRates.inactive}</span>
                        <span className="stat-label-small">Inactive</span>
                      </div>
                    </div>
                  )}
                  {masterDataStats.vatRates.countries > 0 && (
                    <div className="stat-secondary">
                      <FontAwesomeIcon icon={faGlobe} />
                      <div>
                        <span className="stat-value-small">{masterDataStats.vatRates.countries}</span>
                        <span className="stat-label-small">Countries</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="card-actions">
                  <HMSButton
                    variant="primary"
                    size="compact"
                    style={{ flex: 1 }}
                    onClick={(e) => { e.stopPropagation(); history.push('/invoicing/master-data/vat-rates'); }}
                  >
                    <FontAwesomeIcon icon={faArrowRight} /> Manage VAT Rates
                  </HMSButton>
                </div>
              </div>

              {/* Abacus ERP Sync Card */}
              <div 
                className="dashboard-summary-card" 
                style={{ borderTopColor: '#0ea5e9' }}
                onClick={() => history.push('/invoicing/abacus-sync')}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => { if (e.key === 'Enter') history.push('/invoicing/abacus-sync'); }}
              >
                <div className="card-header">
                  <FontAwesomeIcon icon={faSyncAlt} style={{ color: '#0ea5e9' }} />
                  <h3>Abacus</h3>
                  {abacusStats.healthy !== null && (
                    <span className={`master-data-scope-badge ${abacusStats.healthy ? 'scope-entity' : 'scope-global'}`}>
                      <FontAwesomeIcon icon={faServer} /> {abacusStats.healthy ? 'Connected' : 'Disconnected'}
                    </span>
                  )}
                </div>
                <div className="card-stats">
                  <div className="stat-main">
                    <span className="stat-value">{abacusLoading ? '...' : abacusStats.total_synced}</span>
                    <span className="stat-label">In Abacus</span>
                  </div>
                  <div className="stat-secondary">
                    <FontAwesomeIcon icon={faClock} />
                    <div>
                      <span className="stat-value-small">{abacusLoading ? '...' : abacusStats.total_pending}</span>
                      <span className="stat-label-small">Waiting for someone</span>
                    </div>
                  </div>
                  {abacusStats.total_errors > 0 && (
                    <div className="stat-secondary">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <div>
                        <span className="stat-value-small">{abacusStats.total_errors}</span>
                        <span className="stat-label-small">Errors</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="card-actions">
                  <HMSButton
                    variant="primary"
                    size="compact"
                    style={{ flex: 1 }}
                    onClick={(e) => { e.stopPropagation(); history.push('/invoicing/abacus-sync'); }}
                  >
                    <FontAwesomeIcon icon={faArrowRight} /> View Sync Dashboard
                  </HMSButton>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <Footer />
      </div>
    </Layout>
  );
};

export default InvoicingDashboard;

